/**
 * Consent-gated X ads pixel (uwt.js).
 *
 * Same grant as Google Analytics: `budgit_analytics_consent` must be
 * `granted`, and token-link pages never load it (#1216). Nothing here
 * creates `window.twq` or inserts the script before that grant. With no
 * `REACT_APP_X_PIXEL_ID` every call is a no-op.
 */

import { getAnalyticsConsent, subscribeAnalyticsConsent } from './analyticsConsent';
import { isAnalyticsExcludedPath } from '../utils/sensitiveLinkToken';
import { getXAdsPixelIds, isXPixelConfigured, X_PIXEL_SCRIPT_SRC } from './xAdsPixelConfig';
import { registerStoreRedirectHook } from './storeNavigation';
import { markThirdPartyScriptInjected } from './reloadOnConsentWithdrawal';

const FIRED_STORAGE_PREFIX = 'x_ads_pixel_fired:';
const OPAQUE_ID_PREFIX = 'x_ads_pixel_opaque:';
const OPAQUE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let pixelLoadStarted = false;
let pixelConfigured = false;
const firedKeys = new Set();
/** @type {Map<string, () => boolean>} */
const pendingUntilConsent = new Map();
let consentListenerInstalled = false;
let storeHookInstalled = false;

function pathname() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.pathname || '';
}

function consentGranted() {
  return getAnalyticsConsent() === 'granted';
}

export function xPixelMayLoad() {
  if (typeof window === 'undefined') return false;
  if (!isXPixelConfigured()) return false;
  if (!consentGranted()) return false;
  if (isAnalyticsExcludedPath(pathname())) return false;
  return true;
}

function installTwqStub() {
  if (typeof window === 'undefined' || typeof window.twq === 'function') return;
  const twq = function twqStub(...args) {
    if (twq.exe) {
      twq.exe.apply(twq, args);
    } else {
      twq.queue.push(args);
    }
  };
  twq.version = '1.1';
  twq.queue = [];
  window.twq = twq;
}

function hasFired(onceKey) {
  if (firedKeys.has(onceKey)) return true;
  try {
    if (window.sessionStorage && window.sessionStorage.getItem(FIRED_STORAGE_PREFIX + onceKey)) {
      firedKeys.add(onceKey);
      return true;
    }
  } catch (_) {
    // Private mode: the in-memory set is the only guard.
  }
  return false;
}

function markFired(onceKey) {
  firedKeys.add(onceKey);
  try {
    if (window.sessionStorage) {
      window.sessionStorage.setItem(FIRED_STORAGE_PREFIX + onceKey, '1');
    }
  } catch (_) {
    // Ignore quota / private mode.
  }
}

/**
 * Load uwt.js and send twq('config', pixelId). Idempotent.
 * Returns false, and does not touch twq, until analytics consent is granted.
 */
export function enableXPixel() {
  if (!xPixelMayLoad()) return false;
  installTwqStub();

  if (!pixelLoadStarted) {
    const existing = document.querySelector(`script[src="${X_PIXEL_SCRIPT_SRC}"]`);
    if (!existing) {
      const script = document.createElement('script');
      script.async = true;
      script.src = X_PIXEL_SCRIPT_SRC;
      document.head.appendChild(script);
    }
    pixelLoadStarted = true;
    markThirdPartyScriptInjected();
  }

  if (!pixelConfigured) {
    window.twq('config', getXAdsPixelIds().pixelId);
    pixelConfigured = true;
  }
  return true;
}

/** Boot-time policy: load only if previously granted, and wire the store hook. */
export function bootXPixelFromConsent() {
  registerXStoreClickHook();
  if (consentGranted()) {
    enableXPixel();
    flushXEventsAfterConsent();
  }
}

function newOpaqueEventId() {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Random id for X conversion_id. Reused for the same local onceKey so a
 * retry dedupes. Never derived from an account id or a Checkout session id.
 * Kept in this tab only. A later Conversion API can persist it server-side.
 */
function opaqueEventId(onceKey) {
  const storageKey = OPAQUE_ID_PREFIX + onceKey;
  try {
    const existing = window.sessionStorage && window.sessionStorage.getItem(storageKey);
    if (existing && OPAQUE_ID_RE.test(existing)) return existing;
  } catch (_) {
    // Private mode: a fresh id for this call.
  }
  const id = newOpaqueEventId();
  try {
    if (window.sessionStorage) window.sessionStorage.setItem(storageKey, id);
  } catch (_) {
    // Ignore quota / private mode.
  }
  return id;
}

function cleanParams(params) {
  const out = {};
  if (!params) return out;
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  });
  return out;
}

/**
 * twq('event', id, params). No-ops before consent (no twq call).
 * `onceKey` makes a repeated trigger a no-op for the rest of the tab session.
 */
export function trackXEvent(eventId, params, { onceKey } = {}) {
  if (!eventId || typeof window === 'undefined') return false;
  if (onceKey && hasFired(onceKey)) return false;
  if (!enableXPixel()) return false;
  if (typeof window.twq !== 'function') return false;
  if (onceKey) markFired(onceKey);
  window.twq('event', eventId, cleanParams(params));
  return true;
}

function queueUntilConsent(onceKey, fire) {
  if (!isXPixelConfigured()) return false;
  if (hasFired(onceKey)) return false;
  if (typeof window !== 'undefined' && isAnalyticsExcludedPath(pathname())) return false;
  if (!consentGranted()) {
    // No decision yet: hold the event until Accept. A reject drops it.
    if (getAnalyticsConsent() == null) pendingUntilConsent.set(onceKey, fire);
    return false;
  }
  return fire();
}

/** Fire events that happened before Accept, only after the grant. */
export function flushXEventsAfterConsent() {
  if (!xPixelMayLoad() || pendingUntilConsent.size === 0) return;
  const jobs = Array.from(pendingUntilConsent.values());
  pendingUntilConsent.clear();
  jobs.forEach((fire) => {
    try {
      fire();
    } catch (_) {
      // One event must not block the rest.
    }
  });
}

function installConsentListener() {
  if (consentListenerInstalled || typeof window === 'undefined') return;
  consentListenerInstalled = true;
  subscribeAnalyticsConsent((choice) => {
    if (choice === 'granted') {
      enableXPixel();
      flushXEventsAfterConsent();
    } else {
      // Reject (or a reset) drops everything that was held for Accept.
      pendingUntilConsent.clear();
    }
  });
}

installConsentListener();

export function trackXDownloadPageView() {
  return queueUntilConsent('page_view:download', () => trackXEvent(
    getXAdsPixelIds().eventIds.PAGE_VIEW,
    { description: 'download' },
    { onceKey: 'page_view:download' }
  ));
}

/**
 * Download-page store button or mobile auto-redirect. Reached through the
 * beforeStoreRedirect hook, which storeNavigation only runs with consent.
 * `store` is `app_store` or `play_store`.
 */
export function trackXDownloadClick(store) {
  const description = store === 'play_store' ? 'play_store' : 'app_store';
  const onceKey = `download:${description}`;
  return queueUntilConsent(onceKey, () => trackXEvent(
    getXAdsPixelIds().eventIds.DOWNLOAD,
    { description },
    { onceKey }
  ));
}

export function trackXWebSignup() {
  const conversionId = opaqueEventId('signup');
  return queueUntilConsent('signup', () => trackXEvent(
    getXAdsPixelIds().eventIds.SIGN_UP,
    { conversion_id: conversionId },
    { onceKey: 'signup' }
  ));
}

/**
 * Stripe verify-session succeeded. `amountTotalCents` is Stripe's
 * amount_total (smallest currency unit). Value is omitted when the
 * amount or currency is missing. `localDedupeKey` stays in this tab
 * and is never copied onto the X payload.
 */
export function trackXCheckoutSuccess({ amountTotalCents, currency, localDedupeKey } = {}) {
  const onceKey = `purchase:${localDedupeKey || 'once'}`;
  const params = { conversion_id: opaqueEventId(onceKey) };
  // null / undefined / '' mean "no amount". Number() would turn them into 0
  // and send a zero-value purchase.
  const amountMissing = amountTotalCents === null || amountTotalCents === undefined || amountTotalCents === '';
  const cents = amountMissing ? Number.NaN : Number(amountTotalCents);
  const code = typeof currency === 'string' && currency ? currency.toUpperCase() : '';
  if (Number.isFinite(cents) && code) {
    params.value = cents / 100;
    params.currency = code;
  }
  return queueUntilConsent(onceKey, () => trackXEvent(
    getXAdsPixelIds().eventIds.PURCHASE,
    params,
    { onceKey }
  ));
}

/**
 * Register the Download event on the shared beforeStoreRedirect seam.
 * twq is fire-and-forget, so the hook resolves at once and never uses the
 * 300 ms redirect budget. Idempotent; a no-op when unconfigured.
 */
export function registerXStoreClickHook() {
  if (storeHookInstalled || typeof window === 'undefined') return false;
  if (!isXPixelConfigured()) return false;
  storeHookInstalled = true;
  registerStoreRedirectHook(({ platform }) => {
    trackXDownloadClick(platform === 'android' ? 'play_store' : 'app_store');
  });
  return true;
}

export function resetXPixelForTests() {
  pixelLoadStarted = false;
  pixelConfigured = false;
  storeHookInstalled = false;
  firedKeys.clear();
  pendingUntilConsent.clear();
  if (typeof window !== 'undefined') {
    delete window.twq;
  }
}
